// SeedlingOnField.js

// Purpose: This screen displays detailed information about a specific seedling in the field, 
// allowing users to manage its status, evaluations, and potential station creation.

import React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import { getDBURL, correctTimeZone, usePopupFocus
    ,updateStateHandler, updateArrStateHandler, convertDateToDisplayformat, generateSyncId } from "../Utils/Utils";
import { useFetch } from "use-http";
import { useNavigate } from "react-router-dom";
import { Box, Button, Text, Image, Tip, RadioButton } from "grommet";
import ConfirmPopupWindow from "./ConfirmPopupWindow";
import DangerPopupWindow from "./DangerPopupWindow";
// import { updateStateHandler, updateArrStateHandler, convertDateToDisplayformat, generateSyncId } from "../Utils/Utils";
import useTrapFocus from "../Utils/useTrapFocus";
import StationPopup from "./StationPopup";
import { Trophy, Achievement, SubtractCircle, RadialSelected, Dislike, Like, FormClose, CircleInformation} from "grommet-icons";
import EvaluationSummary from "./EvaluationSummary";
import SheetStateButton from "./SheetStateButton";
import OpenSummaryButton from "./OpenSummaryButton";
import { useDataContext } from '../Contexts/DataContext';
import { UserContext } from "../../App";
import { getFruitColorOptions } from "../Utils/RBGUtils";
import RadioWithText from "./RadioWithText";
import DisqualificationReason from "./DisqualificationReason";
import IconTooltipButton  from "./IconTooltipButton";
import SeedlingMultiYearSummary from "./SeedlingMultiYearSummary";
import { useErrorLogger } from './Logger';

const SeedlingOnField = (props) => {
    const {plant, seedlingId, seedlingYear, seedlingRow, seedlingsArr, selectedField, onCreateNewStation, 
        setChosenOption, onStationPopupClose, deleteDisqualReason} = props;
    // console.log('In SeedlingOnField props >> ', props);

    const currentUser = useContext(UserContext);
    const { DBlogError } = useErrorLogger();
    const { currentSeason } = useDataContext();
    // console.log("In current Season >>  ", currentSeason);
    const navigate = useNavigate();
    const [openConfirm, setOpenConfirm] = useState(false); // Confirm Popup Window
    
    const [openStationPopup, setOpenStationPopup] = useState(false);
    const [newStationCreatedSuccessfully, setNewStationCreatedSuccessfully] = useState(false);
   
    const [seedlingEvaluations, setSeedlingEvaluations] = useState([]);
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);  // set selected one in set of loaded evaluations(preharvest)

    const [showEvaluationSummary, setShowEvaluationSummary] = useState(false);
    const [evaluationMaxGrade, setEvaluationMaxGrade] = useState(0);
    const [bestEvaluationNum, setBestEvaluationNum] = useState(0);
    const [seedlingStatus, setSeedlingStatus] = useState(
        {   Id: '00000000-0000-0000-0000-000000000000', 
            Num: 0
        });    
    const [showDisqualificationReason, setShowDisqualificationReason] = useState(false);
    const [showMultiYearSummary, setShowMultiYearSummary] = useState(false);
    
    const [dangerPopupOpen, setDangerPopupOpen] = useState(false); // Danger Popup Window
    const [dangerMessage, setDangerMessage] = useState('');
    const [dangerFuncOnConfirm, setDangerFuncOnConfirm] = useState('');  

// Manage focus when opening modal windows    usePopupFocus(openConfirm, "confirm-msg");
    usePopupFocus(dangerPopupOpen, "danger-msg");
    usePopupFocus(showDisqualificationReason, "disqual-reas");
    usePopupFocus(showEvaluationSummary, "eval-summ");
    usePopupFocus(showMultiYearSummary, "seed-summ");
    usePopupFocus(openStationPopup, "create-station");

    // Ref for the modal and focus trap to keep focus within it  
    const modalRef = useRef(null);
    useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open   // const [showDangerPopup, setShowDangerPopup] = useState(false);
    const hasMounted = useRef(false);

    // console.log("In SeedlingOnField plant >>  ", plant);

    const initEvaluationColorState = () => {
        return [
            {
                Id: plant.Id,
                sheetType: null,
                sheetID: null,
                sheetStatus: null,
                gradeColor: "status-unknown",
                evaluationGrade: 0,
                evaluationYear: null,
                createDate: null,
                seedlingStatus: null,
                seedlingYear: null,            
                sheetColor: "status-unknown",
                sheetName: "new evaluation"
            },
        ];
    }
    const [sheetColor, setSheetColor] = useState(initEvaluationColorState());

    const { get, post } = useFetch(
        getDBURL()
    );  

    const [disqualYear, setDisqualYear] = useState(null);
    const [isButtonHovered, setIsButtonHovered] = useState(false); // new evaluation button

    // size of screen: small - mobile, medium ipad, large - computer
    // const size = useContext(ResponsiveContext);

    useEffect(() => {
        // get all evaluations for seedling
        (async function loadEvaluationsBySeedlingId() {
            try {
                //  console.log("Seedling Id:  "+ plant.Id);
                //  console.log("Seedling :  ",  plant);
                const res = await get("/loadEvaluationsBySeedlingId/seedling_id=" + plant.Id + "/dt=" + Date.now());
                // console.log("evaluations list:  ", res);
                if( res !== undefined && res.length > 0) {
                    setSeedlingEvaluations(res);
                    setEvaluationMaxGrade(res[0].MaxGrade);
                    setBestEvaluationNum(res[0].BestEvaluationId);
                    setSeedlingStatus({Id: res[0].StatusId,
                        Num: res[0].SeedlingStatus
                    })
                }           
            }
            catch (error) {
                console.log("Error in loadEvaluationsBySeedlingId >> ", error.message);
                await DBlogError({
                    error,
                    parameters: JSON.stringify({ seedlingId: plant.Id }),
                    userName: currentUser.email,
                    moduleName: 'SeedlingOnField',
                    functionName: 'loadEvaluationsBySeedlingId',
                });
            } 
        })();
    }, [bestEvaluationNum, plant.StatusID, plant.IsSeeded])

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }

        (async function updateSeedlingFruitColor() {      
            try{
                const updateValues = {
                    Id: plant.Id,
                    FruitColor: plant.FruitColor,
                    LastSyncDate: correctTimeZone(new Date()),
                };
    
                const response = await post(`/updateSeedlingFruitColor/dt=${Date.now()}`, updateValues);
                // Check if the response is successful
                if (!response.success) {
                    console.log("In updateSeedlingFruitColor response   >> ", response.message); 
                    throw new Error(`HTTP error! ${response.message}`);
                }
                props.reRender();
            }
            catch(error)
            {
                console.error(' 🔴 Error in updateSeedlingFruitColor >> ', error.message);
                await DBlogError({
                    error,
                    parameters: JSON.stringify({ plant }),
                    userName: currentUser.email,
                    moduleName: 'SeedlingOnField',
                    functionName: 'updateSeedlingFruitColor',
                });
            }  
        })();  

    }, [plant.FruitColor])
    
    useEffect(() => {
        if (seedlingEvaluations.length > 0)
        {
            let temp = [];
            seedlingEvaluations.map((evaluation) => {                      
                temp.push ({
                    ...evaluation,
                    sheetID: evaluation.EvaluationId,
                    sheetType: evaluation.EvaluationType,
                    sheetStatus: evaluation.EvaluationStatus,
                    gradeColor: 
                        evaluation.EvaluationGrade === 0
                        ? "#CCCCCC"    // "status-unknown"
                        : (evaluation.EvaluationGrade > 0 && evaluation.EvaluationGrade < 35) 
                            ? "#ffc3a1" 
                            :  (evaluation.EvaluationGrade >= 35 && evaluation.EvaluationGrade < 80) 
                                ? "lightyellow" : "#bed3be",
                    evaluationGrade: evaluation.EvaluationGrade,
                    evaluationProgress: evaluation.EvaluationProgress,
                    evaluationYear: evaluation.EvaluationYear,
                    seedlingStatus: evaluation.SeedlingStatus,
                    statusId: evaluation.StatusId,
                    seedlingYear: evaluation.SeedlingYear, 
                    sheetColor:
                        evaluation.EvaluationType === 2
                        ? "#ffc3a1"
                        : evaluation.EvaluationStatus === 5 
                            ? "#bed3be"
                            : evaluation.EvaluationStatus === 4 
                                ? "#f2e394" 
                                : "#CCCCCC",    // "status-unknown",
                    sheetName:
                        evaluation.EvaluationCreateDate !== null ? convertDateToDisplayformat(evaluation.EvaluationCreateDate) : "new evaluation",  // only first evaluation for the seedling
                    sheetFontColor:
                        evaluation.EvaluationType === 2
                        ? "#f55d07"
                        : evaluation.EvaluationStatus === 5 
                            ? "#436343"      // "accent-1"
                            : evaluation.EvaluationStatus === 4 
                                ? "#8b8000"    // "accent-4"
                                : "#666666",    // "status-unknown",                                                            
                })                      
            });
            // console.log("evaluations buttons:  ", temp);
            setSheetColor(temp);
        }
    }, [seedlingEvaluations]);
    // console.log('sheetColor >> ', sheetColor);

    useEffect(() => {
        // runs after the new station was created
        // console.log("new Station was created:", newStationCreatedSuccessfully);  
        if (newStationCreatedSuccessfully) {
            //  console.log("new Station was created Successfully ");            
            props.setSeedlingRow('');
            props.setShow(false);
        }             
    }, [newStationCreatedSuccessfully])

    // hides the "Confirm" popup window 
    const onClose = () => {
      setOpenConfirm(false);
    };

    const onOpenStationPopup = () => {
        setOpenStationPopup(true);
    }

    const onCloseStationPopup = () => { 
        setOpenStationPopup(false);
        setOpenConfirm(false);    
        props.onStationPopupClose();
    };

    // opens a popup window when the "uproot" button is clicked
    const openDangerPopupWindow = (msg, func) => {
        setDangerMessage(msg);
        
        // fires when the "Yes" button is clicked in the "Danger" popup
        setDangerFuncOnConfirm(() => func); // Store the callback function without executing it        
        setDangerPopupOpen(true);
    };

    // hides the "Danger" popup window 
    const onDangerPopupClose = () => {
        setDangerPopupOpen(false);
        setDangerMessage('');    
    };

    // fires when the "Yes" button is clicked in the "Danger" popup
    const onDangerConfirm = () => {
        // console.log(' << in function onDangerConfirm >> '); 
        if (typeof dangerFuncOnConfirm === 'function') {
            dangerFuncOnConfirm();
        } 
        onDangerPopupClose();
        setDangerFuncOnConfirm('');
    };    

    // Function to handle confirmation in the danger popup window
    const doUnDisqualify = async () => {
        setDisqualYear(null);
        await unDisqualify();
    };

    const unDisqualify = async () => {
        const SQLparams = {
            SeedlingId: plant.Id,
            TreatmentId: null,
            StationId: null,
            Year: new Date().getFullYear(),
        };

        await deleteDisqualReason(SQLparams);
        await updatePlantStatus(plant.Id, null);  
    };

    async function uprootSeedling() {
        try {        
            const seedling = {
                Id: plant.Id,
                UprootedAt: correctTimeZone(new Date()),
                UprootedBy: currentUser.email,
                LastSyncDate: correctTimeZone(new Date()),
            };

            // console.log(" 🖖 In uprootSeedlings seedling   >> ", seedling); 
            const response = await post('/uprootSeedlings/dt=' + Date.now(), [seedling]);

            // Check if the response is successful
            if (!response.success) {
                // console.log("In uprootSeedling response   >> ", response.message); 
                throw new Error(`HTTP error! ${response.message}`);
            }

            const uprooted = response.result
            // console.log("In uprootSeedling uprooted   >> ", uprooted); 

            if (uprooted && uprooted > 0) {
                props.reRender();
                props.setShow(false);
            }

        } catch (error) {
            console.error(' 🔴 Error in uprootSeedling >> ', error.message);
            // setShowError(true);
            // setNotificationMessage(' An error occurred in uproot seedling!!');
            await DBlogError({
                error,
                parameters: JSON.stringify({ plant }),
                userName: currentUser.email,
                moduleName: 'SeedlingOnField',
                functionName: 'uprootSeedling',
            });            
        }
    };

    const updatePlantStatus = async (seedlingId, status, seeded) => {
        try {
            const statusForYearExist = seedlingStatus.Id === null || seedlingStatus.Id === '00000000-0000-0000-0000-000000000000' ? false : true;

            let values = null;
            if (!statusForYearExist) {
                // insert values
                values = {
                    Id: generateSyncId(),
                    SeedlingId: seedlingId,
                    Status: status,
                    IsSeeded: seeded === undefined ? null : seeded,
                    CreateDate: correctTimeZone(new Date()),
                    CreatedBy: currentUser.email,
                    Year: new Date().getFullYear(),                    
                    LastSyncDate: correctTimeZone(new Date()),
                };
            } else {
                // update values
                values = {
                    Id: seedlingStatus.Id,
                    SeedlingId: seedlingId,
                    Status: status,
                    StatusUpdatedAt: correctTimeZone(new Date()),
                    StatusUpdatedBy: currentUser.email,                    
                    IsSeeded: seeded === undefined ? -1 : seeded,
                    SeededUpdatedAt: correctTimeZone(new Date()),
                    SeededUpdatedBy: currentUser.email,                    
                    LastSyncDate: correctTimeZone(new Date()),
                };
            }
            // console.log('In updatePlantStatus values  >>>   ', values);
            const response = await post(`/updatePlantStatus/dt=${Date.now()}`, values);  
            
            // Check if the response is successful
            if (!response.success) {
                // console.log("In updatePlantStatus response   >> ", response.message); 
                throw new Error(`HTTP error! ${response.message}`);
            }                         
            
            if (status !== -1) {
                updateStateHandler(props.setPlantInfo,'StatusID', status);
                updateArrStateHandler(props.setSeedlingsArr, 'StatusID', status, el => el.Id === seedlingId);
            }

            // console.log("In updatePlantStatus seeded   >> ", seeded);
            if (seeded !== undefined) {
                const isSeededValue = seeded;
                updateStateHandler(props.setPlantInfo, 'IsSeeded', isSeededValue);
                updateArrStateHandler(props.setSeedlingsArr, 'IsSeeded', isSeededValue, el => el.Id === seedlingId);
            }              

        } catch (error) {
            console.error(' 🔴 Error in updatePlantStatus >> ', error.message);
            await DBlogError({
                error,
                parameters: JSON.stringify({ plant }),
                userName: currentUser.email,
                moduleName: 'SeedlingOnField',
                functionName: 'updatePlantStatus',
            });
        }
    };

    
    const setBestEvaluation = async (sheetID) => {
        // console.log('plant.Id >  ', plant.Id);
        // console.log('sheetID >  ', sheetID);
        // console.log('bestEvaluationNum >  ', bestEvaluationNum);

        try {
            if (sheetID !== bestEvaluationNum) {
                const updateValues = {
                    Id: generateSyncId(),
                    SeedlingId:  plant.Id,
                    BestEvaluationYear: new Date().getFullYear(),
                    BestEvaluationId: sheetID,
                    BestEvaluationDate: correctTimeZone(new Date()),
                    BestEvaluationBy: currentUser.email,
                    LastSyncDate: correctTimeZone(new Date()),
                };
                const response = await post(`/updateSeedlingBestEvaluation/dt=${Date.now()}`, updateValues);
            
                // Check if the response is successful
                if (!response.success) {
                    console.log("In setBestEvaluation response   >> ", response.message); 
                    throw new Error(`HTTP error! ${response.message}`);
                }  

                setBestEvaluationNum(sheetID);
            }
        } catch (error) {
            console.error(' 🔴 Error in setBestEvaluation >> ', error.message);
            await DBlogError({
                error,
                parameters: JSON.stringify({ plant: plant, BestEvaluationID: sheetID }),
                userName: currentUser.email,
                moduleName: 'SeedlingOnField',
                functionName: 'setBestEvaluation',
            });
        }
    };
    
    let disable = false; 

    const openEvaluation = async (evaluation) => 
    {
        // console.log('evaluation>> ', evaluation);
        if (evaluation.EvaluationType === 2) {
            setShowDisqualificationReason(true);
            setDisqualYear(new Date(evaluation.EvaluationCreateDate).getFullYear());
            return;
        }

        disable = false;
        if (evaluation.sheetStatus === 3 || evaluation.sheetStatus === 5)
            disable = true;
        openSheet(evaluation);
    }


    const openEdit = () => 
    {
        disable = false;
        openSheet(selectedEvaluation);
    }   

    // opens a popup window when the "edit" button is clicked
    const openConfirmWindow = (evaluation) => {
        setOpenConfirm(true);
        setSelectedEvaluation(evaluation);
    }

    // fires when the "Confirm" button is clicked in the "Confirm" popup
    const onConfirm = (evaluation) => {
        openEdit(evaluation);
        onClose();
        setSelectedEvaluation(null);
    };
        
    // console.log("selectedEvaluation >> ", selectedEvaluation);
    const openSheet = (evaluation) => {
        // console.log('plant >> ', plant);
        // console.log("addNew >> ", addNew);

        // console.log(' <<--in openSheet f-n --->> ');
        // console.log('evaluation >> ', evaluation); 
        // console.log("seedlingRow >> ", seedlingRow); 
        // console.log("In SeedlingOnField selectedField >> ", selectedField); 
        // console.log("In SeedlingOnField seedlingsArr >> ", seedlingsArr); 

        // if ((evaluation.sheetID === null) || (addNew))
        if (evaluation.sheetID === null)      
            navigate("/preharvest", {
                state: {
                    plantInfo: plant,
                    station: null,
                    seedlingId: seedlingId,
                    seedlingYear: seedlingYear,
                    seedlingRow: seedlingRow,
                    seedlingsArr: seedlingsArr,
                    selectedField: selectedField,
                    editMode: false,
                    editYear: true,
                    disable: disable,
                    sheetName: "EVALUATION",
                    preharvestID: evaluation.sheetID,
                    showMultiYearSummary: showMultiYearSummary,
                },
            });
        else {
            navigate("/preharvest", {
                state: {
                    plantInfo: plant,
                    station: null,
                    seedlingId: seedlingId,
                    seedlingYear: seedlingYear, 
                    seedlingRow: seedlingRow,
                    seedlingsArr: seedlingsArr,
                    selectedField: selectedField,                  
                    editMode: true,
                    disable: disable,
                    sheetName: "EVALUATION",
                    preharvestID: evaluation.sheetID,
                    showMultiYearSummary: showMultiYearSummary,
                },
        });
        }
    };

    const getBoxWidth = (el, sheetColor) => {
        const currentYear = new Date().getFullYear();
        const lastSheetColor = sheetColor[sheetColor.length - 1]?.evaluationYear;
      
        const isCurrentYearEval = el.EvaluationType === 2 && el.evaluationYear === currentYear;
        const isPreviousYearEval = el.EvaluationType === 2 && el.evaluationYear < currentYear;
        const hasValidSheetColor = lastSheetColor !== null;
      
        return (isCurrentYearEval && hasValidSheetColor) || (isPreviousYearEval && hasValidSheetColor)
          ? "20px"
          : "0px";
    };

    let bg = "#bed3be";
    if (plant.StatusID === 3 || plant.IsSeeded === true)
        bg = "#ffc3a1";

    
    const style = {
        border: "2px solid",
        margin: "10px",
    };
    // console.log('sheetColor >> ', sheetColor);

    return (
        <Box 
        id="seed-on-field"
        ref={modalRef}
        tabIndex="-1"
        style={style}
        direction="row"
        align="start"
        background={bg}>
            {openConfirm && (
                <ConfirmPopupWindow
                    msg={
                        "You have chosen to edit a submitted sheet. This might affect the processing flow. Are you sure you want to proceed?"
                    }
                    onClose={onClose}
                    onConfirm={onConfirm}
                />
            )} 
            {dangerPopupOpen && (
                <DangerPopupWindow
                    // msg="This action will permanently remove the disqualification record. Are you sure you want to proceed?"
                    // onClose={() => setDangerMessage(false)}
                    // onConfirm={onDangerConfirm}

                    msg={dangerMessage}
                    onClose={onDangerPopupClose}
                    onConfirm={onDangerConfirm}                    
                />
            )}

            {/* left side  */}          
            <Box position="relative"> 
                <Box width="140px">
                    <Image
                        size="contain"
                        src="SeedlingIcon.png" 
                        width="100px"                                    
                        height="100px"                                
                    />             
                </Box>
                {(plant.StatusID === 1 ) && (
                <Box  align="center" pad={{vertical: "medium"}}>
                    <Achievement  color="#436343" size='large' />                                
                </Box>
                )}
                {(plant.StatusID === 3 ) && (
                <Box  align="center" pad={{vertical: "medium"}} >
                    <Dislike  color="status-critical" size='large' />                                
                </Box>
                )}
                {(plant.IsSeeded === true ) && (
                <Box  align="center"  pad={{vertical: "medium"}} >
                    <RadialSelected  color="status-critical" size='44px' />                                
                </Box>
                )}
            </Box> 
            {/* end of the left side  */}
            <Box  
                background="light-3"            
                width= "100%"
                height="100%"
                pad="xsmall" 
                >
                {/* data about seedling */}
                <Box pad={{vertical: "small"}} align="center" >
                    <Box pad={{horizontal: "xsmall"}} direction="row-responsive">
                        <Box direction="row" >
                            <Text margin={{horizontal: "xsmall"}} size="medium" > Mother:</Text>
                            <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium" > {plant.MotherName} </Text>
                        </Box>
                        <Box direction="row"  >
                            <Text margin={{horizontal: "xsmall"}} size="medium" > Father:</Text>
                            <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium" > {plant.FatherName} </Text>
                        </Box>
                    </Box>            
                    <Box pad={{horizontal: "xsmall"}} direction="row-responsive">
                            <Box direction="row" >
                                <Text margin={{horizontal: "xsmall"}} size="medium"> year: </Text>  
                                <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium"> {plant.Year} </Text> 
                            </Box>
                            <Box direction="row">
                                <Text margin={{horizontal: "xsmall"}} size="medium" > row:</Text>
                                <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium" > {plant.Row} </Text>
                            </Box>                    
                            <Box direction="row" >
                                <Text margin={{horizontal: "xsmall"}} size="medium"> col:</Text>    
                                <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium"> {plant.Column} </Text> 
                            </Box>
                       
                    </Box>

                    <Box background="neutral-3" height="1px" width="40%" margin={{vertical: "xsmall"}} > </Box>

                    <Box pad={{horizontal: "xsmall"}} direction="row-responsive" >
                    {/* !!plant.StatusID: This converts plant.StatusID to a boolean. 
                    If StatusID is not null, undefined, or 0, it evaluates to true; otherwise, it’s false. */}
                        <RadioWithText
                                text="Fruit color:"
                                getOptions={getFruitColorOptions}
                                initValue={plant.FruitColor}
                                stateHandler={props.setPlantInfo}
                                fieldName="FruitColor"
                                padding="none"                     
                                // disable={!!plant.StatusID}
                                disable={false}
                            />
                        </Box>
                </Box>
                {/* end of data about seedling */}

                <Box background="neutral-3" height="2px" margin={{vertical: "small"}}> </Box>

                <Box pad={{vertical: "small"}} align="center"  background="light-4"  direction="row-responsive" justify="around">
                    <Box pad={{horizontal: "xsmall"}} direction="column" >
                        <Box >
                            <Text weight="bold" margin={{vertical: "xsmall"}} size="medium" > Seedling: </Text>
                        </Box>
                        <Box direction="row" pad={{vertical: "xxsmall"}} >
                            <Achievement  color="#436343" size='medium' />
                            <Text  margin={{horizontal: "xsmall"}} size="medium" > excellent </Text>
                        </Box>

                        <Box direction="row"  pad={{vertical: "xxsmall"}} >
                            <Dislike  color="status-critical" size='medium' />
                            <Text  margin={{horizontal: "xsmall"}} size="medium" > disqualified </Text>
                        </Box>   

                        <Box direction="row"  pad={{vertical: "xxsmall"}} >
                            <RadialSelected  color="status-critical" size='medium' />
                            <Text  margin={{horizontal: "xsmall"}} size="medium" > seeded </Text>
                        </Box>                                        
                    </Box>  

                    <Box pad={{horizontal: "xsmall"}} direction="column" >
                        <Box >
                            <Text weight="bold" margin={{vertical: "xxsmall"}} size="medium" > Grades: </Text>
                        </Box>

                        <Box direction="column"  >
                            <Box direction="row"  align="center">
                                <Box pad="xxsmall" width= "30px" background="#ffc3a1" margin="xxsmall" align="center" round={true}>
                                    <Text  size="small" 
                                        fill="horizontal"
                                        // margin={{horizontal: "xsmall"}} 
                                        weight="bold" >{25}
                                    </Text>                                                  
                                </Box>
                                <Text  margin={{horizontal: "xsmall"}} size="medium" > grade  &lt; 35 </Text>                         
                            </Box>                    

                            <Box direction="row" align="center" >
                                <Box pad="xxsmall" width= "30px" background="lightyellow" margin="xxsmall" align="center" round={true}>
                                    <Text  size="small" 
                                        fill="horizontal"
                                        weight="bold" >{40}
                                    </Text>
                                </Box>
                                <Text  margin={{horizontal: "xsmall"}} size="medium" > 35 &le; grade  &lt; 80 </Text>                         
                            </Box>   

                            <Box direction="row" align="center" >
                                <Box pad="xxsmall" width= "30px" background="#bed3be" margin="xxsmall" align="center" round={true}>
                                    <Text  size="small" 
                                        fill="horizontal"
                                        weight="bold" >{85}
                                    </Text>
                                </Box>                    
                                <Text  margin={{horizontal: "xsmall"}} size="medium" > grade  &ge; 80 </Text>                         
                            </Box>                  
                        </Box>
                    </Box> 
                </Box>

                <Box pad={{vertical: "small"}} align="center"  background="light-4"  direction="row-responsive" justify="around">
                    <Box pad={{horizontal: "xsmall"}} direction="column" >
                        <Box >
                            <Text weight="bold" margin={{vertical: "xsmall"}} size="medium" > Evaluation: </Text>
                        </Box>
                        <Box direction="row-responsive">
                            <Box direction="row" pad={{vertical: "xxsmall"}}  >                                             
                                <Box pad="xxsmall" width= "40px" background="#f2e394" margin="xxsmall" align="center" round={true}> </Box>
                                <Text margin={{horizontal: "xsmall"}} size="medium" > In Progress </Text>
                            </Box>                         
                            
                            <Box direction="row" pad={{vertical: "xxsmall"}}  >
                                <Box pad="xxsmall" width= "40px" background="#bed3be" margin="xxsmall" align="center" round={true}> </Box>
                                <Text margin={{horizontal: "xsmall"}} size="medium" > Finished </Text>
                            </Box>

                            <Box direction="row" pad={{vertical: "xxsmall"}}  >
                                <Box pad="xxsmall" width= "40px" background="#ffc3a1" margin="xxsmall" align="center" round={true}> </Box>
                                <Text margin={{horizontal: "xsmall"}} size="medium" > Disqualified </Text>
                            </Box>                             
                        </Box>
                    </Box>                                           
                </Box>            

                <Box background="neutral-3" height="2px" margin={{vertical: "small"}}> </Box>
                
                <Box align="center">
                    {/* summary button */} 
                    <Box pad={{vertical: "xxsmall"}} >
                        <Box pad="small" align="center" justify="between">
                        <Button 
                            tip={
                                {
                                content: (
                                    <Box style={{
                                //     background: "rgba(204, 204, 204, .6)",
                                        background: "WhiteSmoke",
                                        border: "1px solid #00739D",
                                        borderRadius: "8px",
                                        padding: "5px",
                                        fontSize: "16px",
                                        fontWeight: "bold"
                                        }}>
                                        Multi-Year Evaluation Summary
                                    </Box>
                                ),
                                }
                            }

                            label="summary" 
                            style={{padding: "0px 0px 2px 0px", width: "150px"}}
                            boxShadow= "0px 0px 0px 2px #00739D"
                            onClick={() => setShowMultiYearSummary(true)} 
                        />   
                        </Box>                         
                    </Box>
                    {/* end of summary button part */}


                    {/* list of evaluations buttons */}
                    <Box pad={{vertical: "xxsmall"}} >
                        {sheetColor.length > 0 && sheetColor.map (el => 
                        <Box align="center"
                            direction="row-responsive"
                            key={(el.sheetID)}
                            >
                            {/* {console.log('el ==>>  ', el)} */}
                            <Box direction="row" pad="xxsmall" >  
                                {(el.sheetType !== 2 && el.evaluationYear !== null) ? (     
                                    <RadioButton
                                        name="radio"
                                        hoverIndicator
                                        border={{ color: "red", size: "small" }}                                    
                                        style={{color: "red", accentColor: "red", paddingRight: "50px"}}
                                        checked={bestEvaluationNum !== null && el.sheetID === bestEvaluationNum}
                                        onChange={() => setBestEvaluation(el.sheetID)}
                                    />)
                                    :
                                        
                                        <Box width={getBoxWidth(el, sheetColor)} />                                     
                                }

                                <Box
                                    pad={{ horizontal: "xxsmall" }}
                                    width="150px"
                                    >
                                     {!(el.EvaluationCreateDate === null && (currentSeason > new Date().getFullYear()) ) && 
                                    <SheetStateButton evaluationMaxGrade={evaluationMaxGrade} el={el} openSheet={openEvaluation} /> 
                                     }                            
                                </Box>                            
                            </Box> 
                            {(el.sheetType !== 2 && el.evaluationYear !== null) ? (
                            <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" height="40px" > 
                                <OpenSummaryButton 
                                    evaluationMaxGrade={evaluationMaxGrade} el={el} 
                                    showEvaluationSummary = {setShowEvaluationSummary} 
                                    setSelectedEvaluation = {setSelectedEvaluation} />                        
                            
                                {/* evaluation finished */}
                                {(el.sheetStatus === 3  || el.sheetStatus === 5 ) && (
                                    <> 
                                    <Box pad={{horizontal: "xxsmall"}} width= "110px" >
                                    <Box  margin="xxsmall">
                                        <Button                           
                                            color="#f0869c"
                                            style={{color: "#ffffff", padding: 0, fontSize: "16px"}}
                                            primary
                                            // label={plant.Id}
                                            label="edit"
                                            onClick={() => openConfirmWindow(el)}
                                        />
                                        </Box>  
                                    </Box>                                
                                    </>
                                )}

                                {/* evaluation not finished  display evaluation progress*/}
                                {(el.sheetStatus === 4 ) && (
                                <Box pad={{horizontal: "xxsmall"}}  width= "110px" >
                                    <Box  margin="xxsmall" align="center" round={true} 
                                        background={
                                            `radial-gradient(circle farthest-corner at 0% 50%, #bed3be ${Math.round(el.evaluationProgress) === 100 ? el.evaluationProgress : el.evaluationProgress-10}%, #f8facd ${el.evaluationProgress}%, #f2e394 100%)`
                                        }>
                                        <Text 
                                            fill="horizontal"
                                            weight="bold" > {Math.round(el.evaluationProgress)} %
                                        </Text>
                                    </Box>
                                </Box>                                
                                )}                                
                            </Box> 
                            ) :

                            (el.sheetType === 2 && el.evaluationYear === new Date().getFullYear()) ?
                            <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                            <IconTooltipButton

                                doOnClick={() =>  
                                    openDangerPopupWindow("This action will permanently remove the disqualification record. Are you sure you want to proceed?"
                                    , doUnDisqualify)} 
                                iconName = "Like"
                                iconColor = "#ffc3a1"
                                borderColor='#f55d07'
                                size="34px" 
                                tip="Remove Disqualified Status"
                                />                                                
                            </Box>
                            : 
                            <Box></Box>
                            }
                        </Box> 
                                           
                        )}

                        {showEvaluationSummary &&
                        <Box>
                            <EvaluationSummary plant={plant} station={null} treatment={null} setPlantInfo={props.setPlantInfo} 
                                setShowEvaluationSummary={setShowEvaluationSummary}
                                evaluation={(selectedEvaluation.sheetID)}
                            />
                        </Box>
                        }                         

                            {/* currentSeason is the Current Season Year from Season Management */}
                            {(currentSeason === new Date().getFullYear() ) && 
                            (sheetColor[sheetColor.length-1].sheetStatus === 3 || sheetColor[sheetColor.length-1].sheetStatus === 5 ) && (
                                <Box direction="row" >
                                    <Box width="20px" ></Box>
                                    <Box pad="xxsmall" width= "150px" margin={{vertical: "xxsmall"}} >
                                    <Button  
                                        onMouseEnter={() => setIsButtonHovered(true)}
                                        onMouseLeave={() => setIsButtonHovered(false)}                      
                                        color="#CCCCCC"
                                        style={{padding: '2px', color: '#666666', border: '2px solid #666666',
                                                boxShadow: isButtonHovered ? '0px 0px 0px 2px #666666' : 'none',
                                                transition: 'box-shadow 0.2s ease-in-out' // Adds a smooth effect
                                        }}
                                        
                                        primary
                                        label="new evaluation"
                                        onClick={() => openEvaluation(initEvaluationColorState()[0])}        
                                    />
                                    </Box> 
                                </Box>
                            )} 

                            {openStationPopup && 
                            <StationPopup onClose={onCloseStationPopup} 
                                    plantInfo={plant} 
                                    setNewStationCreatedSuccessfully={setNewStationCreatedSuccessfully} 
                                    setChosenOption={setChosenOption}
                                    parentsList={props.parentsList}
                                    // setPostharvestButton={props.setPostharvestButton}
                                    setStationTreatments={props.setStationTreatments}
                                    onCreateNewStation={onCreateNewStation}
                                    reRender={props.reRender}
                                    >
                            </StationPopup>}  

                        {/* Do not display buttons if the seedling is already disqualified */}
                        {(plant.StatusID !== 3) &&
                        <> 
                            <Box direction={props.size === "xsmall" ? "row" : "row-responsive" }>  
                                <Box direction="row">
                                {/* if the seedlings have at least one evaluation, then leave space equal to radio button */}
                                {(sheetColor.length > 0 && sheetColor[sheetColor.length-1].evaluationYear !== null)  &&
                                    <Box width="20px">
                                       
                                    </Box>
                                }    
                                </Box>


                            </Box> 
                        </>

                        }
                    </Box>
                     {/* end of buttons of evaluations */}
                </Box>
               

                <Box background="neutral-3" height="2px" margin={{vertical: "small"}}> </Box>
                
                {/* list of ations buttons */}
                {(currentSeason === new Date().getFullYear() ) && 
                <Box direction= "row" justify="center" pad={{vertical: "xxsmall"}}> 
                    {(plant.StatusID !== 1 && plant.StatusID !== 3 ) && (
                    <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                    <IconTooltipButton                                            
                        doOnClick = {() => updatePlantStatus(plant.Id, 1)}
                        iconName = "Trophy"
                        iconColor = "#bed3be"
                        borderColor='#047343'
                        size="34px" 
                        tip="Mark as Excellent"
                        />                                                
                    </Box> 
                    )}

                    {/* {console.log('plant  >>>   ', plant)} */}
                    {(plant.StatusID === 1) && (
                        <>
                        {/* {(plant.HasStation !== 1) && ( */}
                        <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                        <IconTooltipButton                                            
                            doOnClick = {() => updatePlantStatus(plant.Id, null)}
                            iconName = "Trophy"
                            iconColor = "#bed3be"
                            borderColor="#FF4040"
                            size="34px" 
                            tip="Remove Excellent Status"
                            />                                                
                        </Box>
                        {/* )} */}

                        <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                        <IconTooltipButton                                            
                            doOnClick={onOpenStationPopup}
                            iconName = "Inherit"
                            iconColor = "#DADADA"
                            borderColor="#00739D"
                            size="34px" 
                            tip="Create Station"
                            />                                                
                        </Box>
                    </> 
                    )}
                        



                    {(plant.StatusID !== 1  ) && (
                    (plant.StatusID === 3 ) ?
                        <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                        <IconTooltipButton                                            
                            // doOnClick = {() => setShowDangerPopup(true)}
                            doOnClick={() =>  
                                openDangerPopupWindow("This action will permanently remove the disqualification record. Are you sure you want to proceed?"
                                , doUnDisqualify)} 
                            iconName = "Like"
                            iconColor = "#ffc3a1"
                            borderColor='#f55d07'
                            size="34px" 
                            tip="Remove Disqualified Status"
                            />                                                
                        </Box> 
                        :
                        <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                        <IconTooltipButton                                            
                            doOnClick = {() => {
                                setShowDisqualificationReason(true);
                                setDisqualYear(new Date().getFullYear());}}
                            iconName = "Dislike"
                            iconColor = "#ffc3a1"
                            borderColor='#FF4040'
                            size="34px" 
                            tip="Disqaulify"
                            />                                                
                        </Box>
                    )}

 
                    {(plant.IsSeeded === true ) ?
                    <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                    <IconTooltipButton                                            
                        doOnClick = {() => updatePlantStatus(plant.Id, -1, null)}
                        iconName = "RadialSelected"
                        iconColor = "#ffc3a1"
                        borderColor='#f55d07'
                        size="34px" 
                        tip="Remove Seeded Status"
                        />                                                
                    </Box>
                    :
                    <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                    <IconTooltipButton                                            
                        doOnClick = {() => updatePlantStatus(plant.Id, -1, true)}
                        iconName = "RadialSelected"
                        iconColor = "#ffc3a1"
                        borderColor='#FF4040'
                        size="34px" 
                        tip="Mark as Seeded"
                        />                                                
                    </Box>
                    }

                    <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" margin={{horizontal: "xxsmall"}}>
                    <IconTooltipButton                                            
                        doOnClick={() =>  
                            openDangerPopupWindow("Selected seedling will be uprooted! Are you sure you want to continue?", uprootSeedling)} 
                        iconName = "Trash"
                        iconColor = "#ffc3a1"
                        borderColor='#FF4040'                        
                        size="34px" 
                        tip="Uproot"
                        />                                                
                    </Box>
                     
                </Box>
                }
                {/* list of ations buttons */}


                {showDisqualificationReason &&  
                <DisqualificationReason 
                    plant = {plant}
                    station={null} 
                    treatment={null}
                    updatePlantStatus = {updatePlantStatus}
                    reRenderSourseScreen={props.reRender}
                    onClose={() => setShowDisqualificationReason(false)}
                    disqualYear={disqualYear}
                    disable={false}
                    />
                }         


                {showMultiYearSummary &&
                <SeedlingMultiYearSummary 
                    plant={plant}
                    seedlingId={seedlingId}
                    seedlingYear={seedlingYear}
                    seedlingRow={seedlingRow}
                    seedlingsArr = {seedlingsArr}
                    onClose={() => setShowMultiYearSummary(false)}
                    />
                }


                <Box  pad="small" align="center" justify="between">
                    <Button label="close" 
                        style={{color: "#ffffff", padding: "0px", width: "150px"}}
                        primary onClick={() => props.setShow(false)} 
                    />   
                </Box>              
            </Box>               
        </Box>
    );
};

export default SeedlingOnField;
